import { createSlice, PayloadAction } from '@reduxjs/toolkit'
import { justForSideEffect, justForSideEffectWithPayload } from 'src/infrastructure/saga.helpers'
import {
  RestockingState,
  GenerateListsPayload,
  ItemsListPayload,
  ProductsListPayload,
  SetProductsList,
  SetRestockingItemsList,
  SetRestockingLists,
  UpdateRestockingListPayload,
  ValidateBadgePayload,
  GetProductsListByCSVSagaParams,
  SearchRestockListsPayload,
  SetFiltersSearchLists,
  SetSelectedFiltersHistory,
  SetRestockingListPages,
  RemoveRestockingItemPayload,
  RemoveRestockingItemFromState,
  CompoundType,
  GetAvailableRestockListsPayload,
  SetAvailableRestockLists,
  SetProperty,
  StartRestockingListPagePayload,
  RestockingListTypesEnum,
  StartedPage,
  StartRestockingAddressPayload,
  SetRestockingBatchIsDonePayload,
  CollectRestockingAddress,
  FinishRestockingAddressPayload,
  ItemToRestock,
  SetRestockingItemIsDonePayload,
  ItemRestocking,
  RestockingFullcartPayload,
  ItemsToCollect,
  OperatorUnlinkPayload,
  StartOrFinishRestockingAddressRestockPayload,
  FinishedPage,
  FinishItemRestockUnfit,
  ValidateProductPayload,
  ValidateRestockAddressPayload,
  ReportItemQuantityPerBoxPayload,
  SetRestockingPageDetails,
  CheckInValidatePayload,
  CheckinGenerateRestockListPayload,
  ValidateCollectAddressPayload,
  FinishCollectAddressEmptyGondolaPayload,
  RestockingValidateEntryCollectAddressPayload,
  GetMovingAddressPayload,
  PrintAddressesLabelPayload,
} from './RestockingTypes'
import { cloneDeep } from 'lodash'

export const initialState: RestockingState = {
  productsList: {
    products: [],
    totalSkus: 0,
    totalDemandHigh: 0,
    totalDemandLow: 0,
    totalProgrammed: 0,
    totalNoCapacity: 0,
    level: undefined,
    priority: undefined,
  },
  restockingLists: [],
  restockingListPages: [],
  restockingItemsList: [],
  availableRestockLists: [],
  filtersSearchLists: {
    levels: [],
    priorities: [],
    sourceTypes: [],
    status: [],
    types: [],
  },
  selectedFiltersHistory: {
    idFulfillmentCenter: null,
    createdDate: null,
    priority: [],
    sourceType: [],
    level: [],
    idType: [],
    idStatus: [],
  },
  user: {
    badge: '',
    user: '',
    startedPage: {
      itemsToCollect: null,
      pausedPage: false,
    },
    itemsToRestock: [],
    finishedPage: null,
  },
  autoFetchRestockingLists: false,
  idRestockingType: RestockingListTypesEnum.RESTOCKING_GROCERY,
  hasForkliftAuth: true,
  acceptsPageWithPausedStatus: false,
  isRestockAddressValid: false,
  restockingPageDetails: undefined,
  restockingListTypes: [],
  orderCodes: [],
  movingAddresses: [],
}

const getProductsList = justForSideEffectWithPayload<ProductsListPayload>()
const getProductsListByCSV = justForSideEffectWithPayload<GetProductsListByCSVSagaParams>()
const generateRestockingLists = justForSideEffectWithPayload<GenerateListsPayload>()
const getRestockingListPages = justForSideEffectWithPayload<number>()
const removeRestockingItem = justForSideEffectWithPayload<RemoveRestockingItemPayload>()
const getItemsList = justForSideEffectWithPayload<ItemsListPayload>()
const validateBadge = justForSideEffectWithPayload<ValidateBadgePayload>()
const startRestockingList = justForSideEffectWithPayload<UpdateRestockingListPayload>()
const finishRestockingList = justForSideEffectWithPayload<UpdateRestockingListPayload>()
const searchRestockingLists = justForSideEffectWithPayload<SearchRestockListsPayload>()
const getFiltersSearchLists = justForSideEffectWithPayload<boolean>()
const getAvailableRestockLists = justForSideEffectWithPayload<GetAvailableRestockListsPayload>()
const startRestockingListPage = justForSideEffectWithPayload<StartRestockingListPagePayload>()
const startRestockingAddress = justForSideEffectWithPayload<StartRestockingAddressPayload>()
const finishRestockingAddress = justForSideEffectWithPayload<FinishRestockingAddressPayload>()
const restockingFullcart = justForSideEffectWithPayload<RestockingFullcartPayload>()
const operatorUnlink = justForSideEffectWithPayload<OperatorUnlinkPayload>()
const startRestockingAddressRestock = justForSideEffectWithPayload<StartOrFinishRestockingAddressRestockPayload>()
const finishRestockingAddressRestock = justForSideEffectWithPayload<StartOrFinishRestockingAddressRestockPayload>()
const finishRestockingAddressRestockUnfit = justForSideEffectWithPayload<FinishItemRestockUnfit>()
const restockingValidateProduct = justForSideEffectWithPayload<ValidateProductPayload>()
const restockingValidateRestockAddress = justForSideEffectWithPayload<ValidateRestockAddressPayload>()
const restockingReportQuantityPerBox = justForSideEffectWithPayload<ReportItemQuantityPerBoxPayload>()
const getRestockingPageDetails = justForSideEffectWithPayload<number>()
const getRestockingTypes = justForSideEffect()
const validateBadgeForListCreation = justForSideEffectWithPayload<ValidateBadgePayload>()
const restockingCheckinValidate = justForSideEffectWithPayload<CheckInValidatePayload>()
const generateCheckinRestockList = justForSideEffectWithPayload<CheckinGenerateRestockListPayload>()
const validateCollectAddress = justForSideEffectWithPayload<ValidateCollectAddressPayload>()
const finishCollectAddressEmptyGondola = justForSideEffectWithPayload<FinishCollectAddressEmptyGondolaPayload>()
const restockingValidateEntryCollectAddress = justForSideEffectWithPayload<RestockingValidateEntryCollectAddressPayload>()
const fetchMovingAddress = justForSideEffectWithPayload<GetMovingAddressPayload>()
const printAddressesLabel = justForSideEffectWithPayload<PrintAddressesLabelPayload>()

const setProductsList = (
  state: RestockingState,
  action: PayloadAction<SetProductsList>,
): RestockingState => ({
  ...state,
  productsList: action.payload.productsList,
})

const setRestockingLists = (
  state: RestockingState,
  action: PayloadAction<SetRestockingLists>,
): RestockingState => ({
  ...state,
  restockingLists: action.payload.restockingLists,
})

const setAvailableRestockLists = (
  state: RestockingState,
  action: PayloadAction<SetAvailableRestockLists>,
): RestockingState => ({
  ...state,
  availableRestockLists: action.payload.availableRestockLists,
})

const setRestockingListPages = (
  state: RestockingState,
  action: PayloadAction<SetRestockingListPages>,
): RestockingState => ({
  ...state,
  restockingListPages: action.payload.restockingListPages,
})

const setRestockingItemsList = (
  state: RestockingState,
  action: PayloadAction<SetRestockingItemsList>,
): RestockingState => ({
  ...state,
  restockingItemsList: action.payload.restockingItemsList,
})

const setFilterSearchLocations = (
  state: RestockingState,
  action: PayloadAction<SetFiltersSearchLists>,
): RestockingState => ({
  ...state,
  filtersSearchLists: action.payload.filtersSearchLists,
})

const setSelectedFiltersHistory = (
  state: RestockingState,
  action: PayloadAction<SetSelectedFiltersHistory>,
): RestockingState => ({
  ...state,
  selectedFiltersHistory: action.payload.selectedFiltersHistory,
})

const setAutoFetchRestockingLists = (
  state: RestockingState,
  action: PayloadAction<boolean>,
): RestockingState => ({
  ...state,
  autoFetchRestockingLists: action.payload,
})

const removeRestockingItemFromState = (state: RestockingState, action: PayloadAction<RemoveRestockingItemFromState>): RestockingState => {
  const { id, stateName } = action.payload
  const restockingStateItem = cloneDeep(state[stateName])

  const updatedRestockingItem = (restockingStateItem as CompoundType[]).filter((item) => item.id !== id)

  return {
    ...state,
    [stateName]: updatedRestockingItem,
  }
}

const setProperty = (state: RestockingState, action: PayloadAction<SetProperty>): RestockingState => {
  const { property, value } = action.payload

  return {
    ...state,
    [property]: value,
  }
}

const setUserStartedPage = (state: RestockingState, action: PayloadAction<StartedPage>): RestockingState => ({
  ...state,
  user: {
    ...state.user,
    startedPage: action.payload,
  },
})

const setItemsToCollect = (state: RestockingState, action: PayloadAction<ItemsToCollect | null>): RestockingState => ({
  ...state,
  user: {
    ...state.user,
    startedPage: {
      ...state.user.startedPage,
      itemsToCollect: action.payload,
    },
  },
})

const setUserItemsToRestock = (state: RestockingState, action: PayloadAction<ItemToRestock[]>): RestockingState => {
  const itemsToRestockMapped = action.payload.map((item) => ({ ...item, done: false }))
  return {
    ...state,
    user: {
      ...state.user,
      itemsToRestock: itemsToRestockMapped,
    },
  }
}

const setUserItemToRestockIsDone = (state: RestockingState, action: PayloadAction<number>): RestockingState => {
  const idModel = action.payload

  const cloneState = cloneDeep(state.user.itemsToRestock)

  const currentItemIndex = cloneState.findIndex((item) => item.idModel === idModel)

  if (currentItemIndex === -1) return state

  cloneState[currentItemIndex].done = true

  return {
    ...state,
    user: {
      ...state.user,
      itemsToRestock: cloneState,
    },
  }
}

const setUserItemsToRestockIsDone = (state: RestockingState, action: PayloadAction<ItemToRestock[]>): RestockingState => {
  const itemsToRestock = action.payload

  const cloneState = cloneDeep(state.user.itemsToRestock)

  itemsToRestock.forEach((item) => {
    const currentItemIndex = cloneState.findIndex((stateItem) => stateItem.idModel === item.idModel)

    if (currentItemIndex === -1) return

    cloneState[currentItemIndex].done = true
  })

  return {
    ...state,
    user: {
      ...state.user,
      itemsToRestock: cloneState,
    },
  }
}

const setUserFinishedPage = (state: RestockingState, action: PayloadAction<FinishedPage>): RestockingState => {
  const finishedPage = action.payload
  return {
    ...state,
    user: {
      ...state.user,
      finishedPage,
    },
  }
}

const setAddressStartedPage = (state: RestockingState, action: PayloadAction<CollectRestockingAddress>): RestockingState => {
  const updatedAddress = action.payload

  const cloneState = cloneDeep(state.user)

  if (!cloneState.startedPage.itemsToCollect) return state

  const currentAddressIndex = cloneState.startedPage.itemsToCollect.addresses.findIndex((address) => address.order === updatedAddress.order)

  if (currentAddressIndex === -1) return state

  cloneState.startedPage.itemsToCollect.addresses[currentAddressIndex] = updatedAddress

  return {
    ...state,
    user: cloneState,
  }
}

const setRestockingBatchIsDone = (state: RestockingState, action: PayloadAction<SetRestockingBatchIsDonePayload>): RestockingState => {
  const { addressOrder, idBatch } = action.payload

  const cloneState = cloneDeep(state.currentCollectRestockingAddress)

  if (!cloneState || cloneState.order !== addressOrder) return state

  const currentItemIndex = cloneState.items.findIndex((item) => !item.done)

  if (currentItemIndex === -1) return state

  const currentBatchIndex = cloneState.items[currentItemIndex].batches.findIndex((batch) => batch.id === idBatch)

  if (currentBatchIndex === -1) return state

  cloneState.items[currentItemIndex].batches[currentBatchIndex].done = true

  return {
    ...state,
    currentCollectRestockingAddress: cloneState,
  }
}

const setRestockingItemIsDone = (state: RestockingState, action: PayloadAction<SetRestockingItemIsDonePayload>): RestockingState => {
  const { addressOrder, idModel } = action.payload

  const cloneState = cloneDeep(state.currentCollectRestockingAddress)

  if (!cloneState || cloneState.order !== addressOrder) return state

  if (idModel) {
    cloneState.items = cloneState.items.map((item) => {
      if (item.idModel === idModel) {
        return { ...item, done: true }
      }
      return item
    })

    return {
      ...state,
      currentCollectRestockingAddress: cloneState,
    }
  }

  const currentItemIndex = cloneState.items.findIndex((item) => !item.done)

  if (currentItemIndex === -1) return state

  cloneState.items[currentItemIndex].done = true

  return {
    ...state,
    currentCollectRestockingAddress: cloneState,
  }
}

const updateAddressOnProductNotFound = (state: RestockingState, action: PayloadAction<number>): RestockingState => {
  const addressOrder = action.payload
  const user = cloneDeep(state.user)

  if (!user.startedPage.itemsToCollect) return state

  const currentAddressIndex = user.startedPage.itemsToCollect.addresses.findIndex((address) => address.order === addressOrder)

  if (currentAddressIndex === -1) return state

  user.startedPage.itemsToCollect.addresses[currentAddressIndex].done = true

  return {
    ...state,
    user,
  }
}

const setRestockingAddressIsDone = (state: RestockingState, action: PayloadAction<number>): RestockingState => {
  const addressOrder = action.payload

  const cloneState = cloneDeep(state.currentCollectRestockingAddress)

  if (!cloneState || cloneState.order !== addressOrder) return state

  cloneState.done = true

  return {
    ...state,
    currentCollectRestockingAddress: cloneState,
  }
}

const addItemToCurrentSurplusRestockingAddress = (state: RestockingState, action: PayloadAction<ItemRestocking>): RestockingState => {
  const item = action.payload

  const cloneState = cloneDeep(state.currentCollectRestockingAddress)

  if (!cloneState) return state

  cloneState.items.push(item)

  return {
    ...state,
    currentCollectRestockingAddress: cloneState,
  }
}

const setRestockingPageDetails = (
  state: RestockingState,
  action: PayloadAction<SetRestockingPageDetails>,
): RestockingState => ({
  ...state,
  restockingPageDetails: action.payload.restockingPageDetails,
})

const deleteOrderCode = (state: RestockingState, action: PayloadAction<string>): RestockingState => {
  const { orderCodes } = state
  const orderCode = action.payload

  const updatedOrderCodes = orderCodes.filter((code) => code !== orderCode)

  return {
    ...state,
    orderCodes: updatedOrderCodes,
  }
}

const clearState = (): RestockingState => initialState

const clearProperty = (
  state: RestockingState,
  action: PayloadAction<string | string[]>,
): RestockingState => {
  const newState = cloneDeep(state)

  if (Array.isArray(action.payload)) {
    action.payload.forEach((property) => {
      newState[property] = initialState[property]
    })
  } else {
    newState[action.payload] = initialState[action.payload]
  }

  return newState
}

const slice = createSlice({
  name: 'restocking',
  initialState,
  reducers: {
    getProductsList,
    getProductsListByCSV,
    generateRestockingLists,
    getRestockingListPages,
    removeRestockingItem,
    getItemsList,
    setProductsList,
    setRestockingLists,
    setRestockingItemsList,
    validateBadge,
    startRestockingList,
    finishRestockingList,
    searchRestockingLists,
    setFilterSearchLocations,
    getFiltersSearchLists,
    removeRestockingItemFromState,
    setSelectedFiltersHistory,
    setRestockingListPages,
    setAutoFetchRestockingLists,
    getAvailableRestockLists,
    setAvailableRestockLists,
    setProperty,
    clearState,
    clearProperty,
    startRestockingListPage,
    setUserStartedPage,
    setUserItemsToRestock,
    startRestockingAddress,
    setRestockingBatchIsDone,
    setRestockingItemIsDone,
    setRestockingAddressIsDone,
    setAddressStartedPage,
    finishRestockingAddress,
    addItemToCurrentSurplusRestockingAddress,
    restockingFullcart,
    setItemsToCollect,
    operatorUnlink,
    startRestockingAddressRestock,
    setUserItemToRestockIsDone,
    setUserItemsToRestockIsDone,
    finishRestockingAddressRestock,
    setUserFinishedPage,
    finishRestockingAddressRestockUnfit,
    restockingValidateProduct,
    updateAddressOnProductNotFound,
    restockingValidateRestockAddress,
    restockingReportQuantityPerBox,
    getRestockingPageDetails,
    setRestockingPageDetails,
    getRestockingTypes,
    validateBadgeForListCreation,
    restockingCheckinValidate,
    deleteOrderCode,
    generateCheckinRestockList,
    validateCollectAddress,
    finishCollectAddressEmptyGondola,
    restockingValidateEntryCollectAddress,
    fetchMovingAddress,
    printAddressesLabel,
  },
})

export default slice.reducer

export const { actions } = slice
